import { css } from '@emotion/react'
import { memo, useCallback, useEffect, useRef } from 'react'
import { openPm } from '../../../../actions/message'
import { sendPrivateMessage } from '../../../../actions/sendPrivateMessage'
import i18n from '../../../../utilities/i18n'
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState'
import { useHealthAppStateSelector } from '../../../Hooks/useHealthAppState'
import { useUserSelector } from '../../../Hooks/useUser'
import FlexGrid from '../../../common/FlexGrid'
import { COMPONENT_HEIGHTS, PAPER_HEIGHTS } from '../../../theme'
import Authentication from '../../Authentication'
import Compose from '../Compose'
import Chat from '../Rooms/Chat'

const chatGridStyles = css({
    position: 'relative',
    flexGrow: 1,
    flexBasis: '100%',
    height: '100%',
    minHeight: 0,
    zIndex: PAPER_HEIGHTS.zero,
})

interface IPrivateMessage {
    id: number
    height: number
}

const PrivateMessage = memo((props: IPrivateMessage) => {
    const { id, height } = props
    const chatRef = useRef(null)
    const authenticated = useAppStateSelector(AppStateType.APP, 'authenticated')
    const socketConnected = useHealthAppStateSelector('connected')
    const slug = useUserSelector(id, 'slug')

    const loaded = socketConnected
    const chatHeight =
        height > COMPONENT_HEIGHTS.compose
            ? height - COMPONENT_HEIGHTS.compose
            : 0
    const styleHeight = chatHeight > 0 ? chatHeight + 'px' : undefined

    useEffect(() => {
        if (!slug || !socketConnected || !authenticated) return
        openPm(slug, {
            noVideo: true,
            skipSideEffects: true,
        })
    }, [slug, socketConnected, authenticated])

    const onSend = useCallback((id: number, text: string) => {
        sendPrivateMessage(id, text)
        chatRef.current?.scrollToBottom?.()
    }, [])

    if (!authenticated) {
        return (
            <Authentication subject={i18n.t('messages.sendPrivateMessages')} />
        )
    }

    if (!id) return null

    return (
        <FlexGrid overrides={chatGridStyles} key={id}>
            <Chat styleHeight={styleHeight} chatRef={chatRef} id={id} pm />
            <Compose
                id={id}
                disabled={!loaded}
                onSend={onSend}
                placeholder={i18n.t('chat.sendPrivateMessage')}
                disableKeyboardOpenDetection
                noFullWindowButtons
                hideTipButton
                pm
            />
        </FlexGrid>
    )
})

export default PrivateMessage
